// Shared types + chart palette for the Material Cost Analysis report.
// All decimals arrive as strings and gRPC may omit zero-valued fields, so every
// reader goes through `num` (re-exported from the 3M shared helpers) and
// defaults missing values to 0.

export {
  num,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  computeParetoAbc,
} from "../ThreeMConsolidation/threeMShared";

// One flat row of the all-materials task breakdown (Excel export): the
// per-material drill-down rows plus the material identity, straight from
// GET 3m-consolidation/material-breakdown-all/:projectId.
export interface MaterialBreakdownAllRow {
  materialId: number;
  materialName?: string;
  materialCode?: string;
  uomName?: string;
  categoryId?: number;
  categoryName?: string;
  phaseId?: number;
  phaseName?: string;
  activityId?: number;
  activityName?: string;
  taskId?: number;
  taskName?: string;
  taskSequenceId?: string;
  totalQuantity?: string;
  totalCost?: string;
  lineCount?: number;
}

export interface MaterialSummaryRow {
  materialId: number;
  materialName?: string;
  materialCode?: string;
  uomName?: string;
  categoryId?: number;
  categoryName?: string;
  totalQuantity?: string;
  avgRate?: string;
  totalCost?: string;
  taskCount?: number;
  lineCount?: number;
}

// One month of a material's schedule, from
// GET 3m-consolidation/material-monthly/:projectId/:materialId.
export interface MaterialMonthRow {
  month: string; // 'YYYY-MM-01'
  monthQuantity?: string;
  monthCost?: string;
  taskCount?: number;
}

// Register-side totals for the same material, so the drawer can explain any
// difference between the sum of its month cells and the register row instead
// of leaving it unexplained. noSchedule = tasks with no task_schedules rows;
// noWorkQty = tasks whose zero work quantity makes their monthly share
// undefined, dropping their cost out of every month.
export interface MaterialScheduleCoverage {
  registerQuantity?: string;
  registerCost?: string;
  taskCount?: number;
  noScheduleTaskCount?: number;
  noScheduleCost?: string;
  noWorkQtyTaskCount?: number;
  noWorkQtyCost?: string;
}

// One (material, month) cell of the all-materials grid, from
// GET 3m-consolidation/material-monthly-all/:projectId. materialId 0 is the
// unlinked-material bucket. Feeds the Excel "Month-wise Register" pivot.
export interface MaterialMonthCellRow {
  materialId: number;
  month: string; // 'YYYY-MM-01'
  monthQuantity?: string;
  monthCost?: string;
}

export interface MaterialMonthlyScheduleData {
  materialId?: number;
  scheduledQuantity?: string;
  scheduledCost?: string;
  months?: MaterialMonthRow[];
  coverage?: MaterialScheduleCoverage;
}

export interface MaterialSummaryData {
  projectName?: string;
  totalCost?: string;
  materialCount?: number;
  materials?: MaterialSummaryRow[];
}

// 3M accent palette, reordered so no two CVD-confusable hues (red/green,
// green/orange) sit on adjacent ranks in the bar chart / donut. Grey is
// reserved for the aggregated "Others" slice only.
export const CHART_PALETTE = [
  "#389e0d",
  "#1677ff",
  "#fa8c16",
  "#722ed1",
  "#f5222d",
];

export const OTHERS_COLOR = "#8c8c8c";
